import Phaser from 'phaser';
import { GAME_CONFIG } from '../config';
import { Hat } from '../objects/Hat';
import { Character } from '../objects/Character';
import { TrajectorySystem } from './TrajectorySystem';
import { VelocitySample } from '../types';

export class ThrowSystem {
  scene: Phaser.Scene;
  hat: Hat;
  character: Character;

  isDragging: boolean = false;
  velocityHistory: VelocitySample[] = [];
  dragOffset = { x: 0, y: 0 };

  idealForce: number = GAME_CONFIG.IDEAL_FORCE;
  tolerance: number = GAME_CONFIG.FORCE_TOLERANCE;
  lateralSensitivity: number = GAME_CONFIG.LATERAL_SENSITIVITY;
  hitWindowMult: number = 1.0;

  constructor(scene: Phaser.Scene, hat: Hat, character: Character) {
    this.scene = scene;
    this.hat = hat;
    this.character = character;
    this.setupInput();
  }

  private setupInput() {
    this.scene.input.on('pointerdown', this.onPointerDown, this);
    this.scene.input.on('pointermove', this.onPointerMove, this);
    this.scene.input.on('pointerup', this.onPointerUp, this);
    this.scene.input.on('pointerupoutside', this.onPointerUp, this);
  }

  private onPointerDown(pointer: Phaser.Input.Pointer) {
    if (this.hat.state !== 'idle') return;
    if (!this.isOnHat(pointer.x, pointer.y)) return;

    this.isDragging = true;
    this.dragOffset.x = pointer.x - this.hat.screenX;
    this.dragOffset.y = pointer.y - this.hat.screenY;
    this.velocityHistory = [{ x: pointer.x, y: pointer.y, t: performance.now() }];
    this.hat.setState('dragging');
  }

  private onPointerMove(pointer: Phaser.Input.Pointer) {
    if (!this.isDragging) return;

    const newX = pointer.x - this.dragOffset.x;
    const newY = pointer.y - this.dragOffset.y;
    this.hat.setScreenPos(newX, newY);

    const now = performance.now();
    this.velocityHistory.push({ x: pointer.x, y: pointer.y, t: now });
    this.velocityHistory = this.velocityHistory.filter((p) => now - p.t < 100);
  }

  private onPointerUp(_pointer: Phaser.Input.Pointer) {
    if (!this.isDragging) return;
    this.isDragging = false;

    const now = performance.now();
    const recent = this.velocityHistory.filter((p) => now - p.t < 80);
    if (recent.length < 2) {
      this.hat.reset();
      return;
    }

    let rawVx = 0;
    let rawVy = 0;
    for (let i = 1; i < recent.length; i++) {
      const a = recent[i - 1];
      const b = recent[i];
      const dt = (b.t - a.t) / 1000;
      if (dt < 0.005) continue;
      const vy = (b.y - a.y) / dt;
      if (vy < rawVy) {
        rawVy = vy;
        rawVx = (b.x - a.x) / dt;
      }
    }

    if (rawVy >= 0) {
      this.hat.reset();
      return;
    }

    const speed = Math.sqrt(rawVx * rawVx + rawVy * rawVy);
    if (speed < GAME_CONFIG.MIN_THROW_SPEED) {
      this.hat.reset();
      return;
    }

    this.executeThrow(rawVx, rawVy);
  }

  private executeThrow(vx: number, vy: number) {
    const headPos = this.character.getHeadWorldPos();
    const config = TrajectorySystem.makeConfigFromGameConfig();
    config.idealForce = this.idealForce;
    config.tolerance = this.tolerance;
    config.lateralSensitivity = this.lateralSensitivity;
    config.hitWindowMult = this.hitWindowMult;

    const trajectory = TrajectorySystem.calculate(
      vx,
      vy,
      this.hat.screenX,
      this.hat.screenY,
      headPos,
      config,
    );

    this.hat.setFlying(trajectory);
  }

  private isOnHat(x: number, y: number): boolean {
    const halfW = 110 * Math.max(0.5, this.hat.currentScale);
    const halfH = 80 * Math.max(0.5, this.hat.currentScale);
    return Math.abs(x - this.hat.screenX) < halfW && Math.abs(y - this.hat.screenY) < halfH;
  }

  destroy() {
    this.scene.input.off('pointerdown', this.onPointerDown, this);
    this.scene.input.off('pointermove', this.onPointerMove, this);
    this.scene.input.off('pointerup', this.onPointerUp, this);
    this.scene.input.off('pointerupoutside', this.onPointerUp, this);
  }
}
